import { toast } from 'sonner';
import { captureError } from '@/utils/sentry';
import type {
  IBillingService,
  StartCheckoutRequest,
  StartCheckoutResponse,
  VerifySessionRequest,
  VerifySessionResponse,
  RedeemVoucherRequest,
  RedeemVoucherResponse,
} from './types';
import { v1, toResult } from '@/infrastructure/api/v1/client';

/**
 * Normalize backend errors into user-friendly messages
 */
function normalizeError(error: unknown): string {
  if (error instanceof Error) {
    // Check for common error patterns
    if (error.message.includes('voucher') || error.message.includes('code')) {
      return 'Invalid or expired voucher code';
    }
    if (error.message.includes('checkout') || error.message.includes('billing')) {
      return 'Checkout failed. Please try again';
    }
    if (error.message.includes('session')) {
      return 'Unable to verify payment session';
    }
    if (error.message.includes('network') || error.message.includes('fetch')) {
      return 'Network error. Please check your connection';
    }
    return error.message;
  }
  return 'An unexpected error occurred';
}

/**
 * Show error toast with normalized message and capture to Sentry
 */
function handleError(error: unknown, operation: string): never {
  const message = normalizeError(error);
  captureError(error, { domain: 'billing', operation });
  toast.error(message);
  throw new Error(message);
}

/**
 * Billing Service Implementation
 * 
 * Handles all billing-related operations including:
 * - Starting Stripe checkout sessions
 * - Verifying payment sessions
 * - Redeeming voucher codes
 */
export class BillingService implements IBillingService {
  /**
   * Start Stripe Checkout flow
   * 
   * @param request - { tier: 'creator' | 'pro', term: 'month' | 'year', orgInstanceId: string }
   * @returns Promise with Stripe Checkout URL
   * 
   * @example
   * const { checkoutUrl } = await billingService.startCheckout({ 
   *   tier: 'creator', 
   *   term: 'month',
   *   orgInstanceId: '123e4567-...'
   * });
   * window.location.href = checkoutUrl;
   */
  async startCheckout(request: StartCheckoutRequest): Promise<StartCheckoutResponse> {
    try {
      // The client id is Stripe's idempotency key: a retry of this request returns the same session
      const { data, error } = await toResult(v1.POST('/api/v1/orgs/{orgInstanceUniqueId}/checkout-sessions', {
        params: { path: { orgInstanceUniqueId: request.orgInstanceId } },
        body: { uniqueId: crypto.randomUUID(), tier: request.tier, term: request.term },
      }));
      if (error) throw error;
      if (!data.checkoutUrl) throw new Error('No checkout URL returned from server');
      return data;
    } catch (error) {
      console.error('Starting checkout failed:', error);
      return handleError(error, 'startCheckout');
    }
  }

  /**
   * Verify Stripe Checkout Session after successful payment
   * 
   * @param request - { session_id: string } - session_id from Stripe redirect
   * @returns Promise with success status
   * 
   * @example
   * const { success } = await billingService.verifySession({ session_id: 'cs_test_...' });
   */
  async verifySession(request: VerifySessionRequest): Promise<VerifySessionResponse> {
    try {
      const { error } = await toResult(v1.POST('/api/v1/checkout-sessions/confirm', { body: { sessionId: request.session_id } }));
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Confirming the checkout session failed:', error);
      return handleError(error, 'verifySession');
    }
  }

  /**
   * Redeem a voucher code to upgrade plan
   * 
   * @param request - { voucherCode: string, orgInstanceId: string }
   * @returns Promise with subscription details
   * 
   * @example
   * const result = await billingService.redeemVoucher({ 
   *   voucherCode: 'CREATOR2024',
   *   orgInstanceId: '123e4567-e89b-12d3-a456-426614174000'
   * });
   */
  async redeemVoucher(request: RedeemVoucherRequest): Promise<RedeemVoucherResponse> {
    try {
      const { data, error } = await toResult(v1.POST('/api/v1/orgs/{orgInstanceUniqueId}/voucher-redemptions', {
        params: { path: { orgInstanceUniqueId: request.orgInstanceId } },
        body: { uniqueId: crypto.randomUUID(), voucherCode: request.voucherCode },
      }));
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Redeeming the voucher failed:', error);
      return handleError(error, 'redeemVoucher');
    }
  }
}

